/**
 * Loads the JSON the fetcher publishes. `VITE_DATA_BASE` points at it; in
 * production that is `/data` on the same CloudFront domain, and for local dev
 * against a dry run it is e.g. `../fetcher/out/data`.
 */

import type {
  LeagueHistory,
  Manifest,
  MatchupReport,
  MatchupReportIndex,
  PayoutStructure,
  SeasonArchive,
  SeasonDues,
  SeasonManagerAliases,
  SeasonPayoutWinners,
} from "@averagejoesff/shared";
import { DATA_PATHS } from "@averagejoesff/shared";

const DATA_BASE = (import.meta.env.VITE_DATA_BASE ?? "/data").replace(/\/+$/, "");
const dataUrl = (path: string) => `${DATA_BASE}/${path}`;

/**
 * Returns `null` when nothing has been published there yet, `T` otherwise.
 *
 * A missing object isn't reliably a 404: CloudFront's SPA-fallback rule
 * (`custom_error_response`, see `infra/cloudfront.tf`) rewrites S3's 403/404
 * for a missing key into a 200 response whose body is `index.html` — so a
 * plain `!res.ok` check can't tell "not found" apart from a real 200. The
 * `content-type` header can, though: every JSON file this app publishes is
 * written with `Content-Type: application/json` (see `S3DataStore`), while
 * the SPA-fallback response is `text/html`.
 */
async function getJson<T>(url: string): Promise<T | null> {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  const contentType = res.headers.get("content-type") ?? "";
  if (!res.ok || !contentType.includes("json")) return null;
  return (await res.json()) as T;
}

export function fetchManifest(): Promise<Manifest | null> {
  return getJson<Manifest>(dataUrl(DATA_PATHS.manifest));
}

export function fetchSeason(year: number): Promise<SeasonArchive | null> {
  return getJson<SeasonArchive>(dataUrl(DATA_PATHS.season(year)));
}

/** The league's current prize payout structure — not tied to a season. */
export function fetchPayouts(): Promise<PayoutStructure | null> {
  return getJson<PayoutStructure>(dataUrl(DATA_PATHS.payouts));
}

/** That season's buy-in payment status per team. */
export function fetchDues(season: number): Promise<SeasonDues | null> {
  return getJson<SeasonDues>(dataUrl(DATA_PATHS.dues(season)));
}

/** That season's manager real-name overrides. */
export function fetchManagerAliases(season: number): Promise<SeasonManagerAliases | null> {
  return getJson<SeasonManagerAliases>(dataUrl(DATA_PATHS.managerAliases(season)));
}

/** That season's manually-assigned prize winners. */
export function fetchPayoutWinners(season: number): Promise<SeasonPayoutWinners | null> {
  return getJson<SeasonPayoutWinners>(dataUrl(DATA_PATHS.payoutWinners(season)));
}

/** Which weekly matchup reports have been published, newest first. */
export function fetchMatchupIndex(): Promise<MatchupReportIndex | null> {
  return getJson<MatchupReportIndex>(dataUrl(DATA_PATHS.matchupIndex));
}

/** One week's skill-authored matchup report. */
export function fetchMatchupReport(season: number, week: number): Promise<MatchupReport | null> {
  return getJson<MatchupReport>(dataUrl(DATA_PATHS.matchupReport(season, week)));
}

/** Every game since 2016 with managers resolved across seasons (see `LeagueHistory`). */
export function fetchLeagueHistory(): Promise<LeagueHistory | null> {
  return getJson<LeagueHistory>(dataUrl(DATA_PATHS.leagueHistory));
}
